import {
    autoUpdate,
    flip,
    offset,
    Placement,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
} from '@floating-ui/react';
import React, { createContext, ReactElement, useContext, useState } from 'react';
import type { PropsWithChildren } from 'react';
import styled from 'styled-components';

import { Button, ButtonSize } from './button';
import { IconDots } from './icon';
import { HorizontalLayout } from './Layout';
import { Text } from './Text';

const FlyoutContext = createContext<{
    setOpen?: (open: boolean) => void;
}>({});

export const FlyoutMenu = ({
    children,
    placement = 'bottom-end',
    buttonSize = 'icon',
    buttonType = 'ghost',
}: PropsWithChildren<{
    placement?: Placement;
    buttonSize?: ButtonSize;
    buttonType?: ThemeButtonOptions;
}>) => {
    const [open, setOpen] = useState(false);

    const { x, y, refs, strategy, context } = useFloating({
        placement,
        open,
        onOpenChange: setOpen,
        middleware: [offset(8), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([useClick(context), useDismiss(context)]);

    return (
        <FlyoutContext.Provider value={{ setOpen }}>
            <div {...getReferenceProps({ ref: refs.setReference, onClick: (event) => event.stopPropagation() })}>
                <Button icon={<IconDots />} size={buttonSize} buttonType={buttonType} />
            </div>
            {open && (
                <Container
                    {...getFloatingProps({
                        ref: refs.setFloating,
                        style: {
                            position: strategy,
                            top: y ?? 0,
                            left: x ?? 0,
                        },
                    })}
                >
                    {children}
                </Container>
            )}
        </FlyoutContext.Provider>
    );
};

export const FlyoutItem = ({
    color,
    disabled,
    onClick,
    icon,
    children,
}: PropsWithChildren<{
    color?: ThemeTextOptions;
    icon?: ReactElement;
    disabled?: boolean;
    onClick?: (event: React.SyntheticEvent) => void;
}>) => {
    const { setOpen } = useContext(FlyoutContext);

    return (
        <StyledFlyoutItem
            $color={color}
            $disabled={disabled}
            onClick={(event: React.SyntheticEvent) => {
                event.stopPropagation();
                if (!disabled) {
                    onClick?.(event);
                    setOpen?.(false);
                }
            }}
            $apart
            $stretch
            $rowCenter
            $padding="8px 24px"
            $gap={8}
        >
            <Text>{children}</Text>
            {icon}
        </StyledFlyoutItem>
    );
};

const StyledFlyoutItem = styled(HorizontalLayout)<{ $disabled?: boolean; $color?: ThemeTextOptions }>`
    ${({ $disabled }) => $disabled && 'font-style: italic'};
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    color: ${({ theme, $color = 'primary', $disabled }) => ($disabled ? theme.text.subtle : theme.text[$color])};

    &:hover {
        background-color: ${({ theme }) => theme.container.secondaryBackground};
    }
`;

const Container = styled.div`
    z-index: 10;
    padding: 0.5rem 0;
    min-width: 14rem;
    border-radius: 3px;
    border: 1px solid ${({ theme }) => theme.border.light};
    background-color: ${({ theme }) => theme.container.primaryBackground};
    box-shadow: 0 2px 6px 0 ${({ theme }) => theme.colors.cocoa900 + theme.alpha[15]};
`;
